import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Send, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  });
  const [consentGiven, setConsentGiven] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        company: formData.company || null,
        subject: formData.subject,
        message: formData.message,
        consent_given: consentGiven,
        consent_timestamp: new Date().toISOString()
      });
      
      toast.success('Message sent successfully! Our team will get back to you within 24 hours.');
      setIsSubmitted(true);
      setConsentGiven(false);
      setFormData({ name: '', email: '', company: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="grid-pattern bg-[var(--bg-primary)] min-h-screen pt-28 md:pt-36">
      {/* Header */}
      <section className="px-4 md:px-8 lg:px-12 pb-12 md:pb-20">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold mb-4 md:mb-6">
            Get in <span className="text-blue-gradient">Touch</span>
          </h1>
          <p className="text-base md:text-lg lg:text-xl text-white/70">
            Have questions about Parler Cloud? Our team is here to help you find the perfect solution.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="px-4 md:px-8 lg:px-12 pb-16 md:pb-24">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Contact Info */}
          <div className="order-2 lg:order-1">
            <h2 className="text-xl md:text-2xl font-semibold mb-6 md:mb-8">
              Contact Information
            </h2>

            <div className="space-y-6 md:space-y-8">
              <div className="flex gap-4 md:gap-5">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-[rgba(42,95,246,0.1)] flex items-center justify-center flex-shrink-0 rounded-xl">
                  <Mail size={22} color="#2a5ff6" />
                </div>
                <div>
                  <h3 className="text-base md:text-lg font-semibold mb-1">Email Us</h3>
                  <p className="text-white/70 text-sm md:text-base">cloud@parler.com</p>
                  <p className="text-white/50 text-xs md:text-sm">We respond within 24 hours</p>
                </div>
              </div>

              <div className="flex gap-4 md:gap-5">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-[rgba(42,95,246,0.1)] flex items-center justify-center flex-shrink-0 rounded-xl">
                  <MapPin size={22} color="#2a5ff6" />
                </div>
                <div>
                  <h3 className="text-base md:text-lg font-semibold mb-1">Headquarters</h3>
                  <p className="text-white/70 text-sm md:text-base">Plano, TX</p>
                  <p className="text-white/50 text-xs md:text-sm">United States</p>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="mt-8 md:mt-12 p-5 md:p-8 bg-[var(--bg-secondary)] border border-white/10 rounded-2xl">
              <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4">Enterprise Solutions</h3>
              <p className="text-white/70 text-xs md:text-sm leading-relaxed mb-4 md:mb-5">
                Looking for custom solutions, dedicated infrastructure, or volume discounts? Our enterprise team can help design a solution tailored to your needs.
              </p>
              <a
                href="mailto:cloud@parler.com"
                className="btn-secondary inline-flex text-xs md:text-sm py-2 px-4"
              >
                Contact Enterprise Sales
                <ArrowRight size={14} />
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="order-1 lg:order-2">
            <div className="bg-[var(--bg-secondary)] border border-white/10 rounded-2xl p-5 md:p-8 lg:p-10">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle size={32} className="text-green-500" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-semibold mb-3">Thank You!</h2>
                  <p className="text-white/70 mb-6">
                    Your message has been sent successfully. Our team will get back to you within 24 hours.
                  </p>
                  <Button
                    onClick={() => setIsSubmitted(false)}
                    className="btn-secondary"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <>
                  <h2 className="text-lg md:text-xl font-semibold mb-6 md:mb-8">
                    Send us a message
                  </h2>

                  <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name" className="block mb-2 text-sm text-white/70">
                          Full Name *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="John Doe"
                          className="bg-white/5 border-white/10 rounded-xl text-white placeholder:text-white/30"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="block mb-2 text-sm text-white/70">
                          Email *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="john@company.com"
                          className="bg-white/5 border-white/10 rounded-xl text-white placeholder:text-white/30"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="company" className="block mb-2 text-sm text-white/70">
                          Company
                        </Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Company Inc."
                          className="bg-white/5 border-white/10 rounded-xl text-white placeholder:text-white/30"
                        />
                      </div>
                      <div>
                        <Label htmlFor="subject" className="block mb-2 text-sm text-white/70">
                          Subject *
                        </Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          placeholder="How can we help?"
                          className="bg-white/5 border-white/10 rounded-xl text-white placeholder:text-white/30"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="block mb-2 text-sm text-white/70">
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        placeholder="Tell us about your project and requirements..."
                        className="bg-white/5 border-white/10 rounded-xl text-white placeholder:text-white/30 resize-none"
                      />
                    </div>

                    {/* GDPR/CCPA Consent Checkbox */}
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="consent"
                        data-testid="contact-consent-checkbox"
                        checked={consentGiven}
                        onCheckedChange={(checked) => setConsentGiven(checked === true)}
                        className="mt-1 border-white/30 data-[state=checked]:bg-[#2a5ff6] data-[state=checked]:border-[#2a5ff6]"
                        required
                      />
                      <Label 
                        htmlFor="consent" 
                        className="text-xs md:text-sm text-white/70 leading-relaxed cursor-pointer"
                      >
                        I consent to Parler Cloud collecting and storing my data from this form. 
                        I understand my information will be used to respond to my inquiry and may be 
                        used in accordance with the{' '}
                        <Link 
                          to="/privacy-policy" 
                          className="text-[#2a5ff6] hover:underline"
                          target="_blank"
                        >
                          Privacy Policy
                        </Link>
                        . I can withdraw my consent at any time by contacting{' '}
                        <a 
                          href="mailto:privacy@parler.com" 
                          className="text-[#2a5ff6] hover:underline"
                        >
                          privacy@parler.com
                        </a>
                        . *
                      </Label>
                    </div>

                    <Button
                      type="submit"
                      data-testid="contact-submit-button"
                      disabled={isSubmitting || !consentGiven}
                      className="btn-primary w-full justify-center h-12 text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                      <Send size={18} />
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
